import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';

// Free-form yyyy-mm-dd date input with auto-formatting and an optional
// calendar picker. Drop-in replacement for `<input type="date">`:
//
//   <DateInput value={form.x} onChange={(e) => setX(e.target.value)} />
//
// The onChange handler receives a synthetic event with `.target.value`
// so existing handlers that pull value off the event still work. The
// value is always either '' or 'yyyy-mm-dd'.
//
// Behaviour:
//   • Typing digits auto-formats — "20260101" becomes "2026-01-01" as
//     the user types. Existing dashes the user types are accepted too.
//   • Empty input is treated as "no value" (valid).
//   • On blur, the entered string is validated as a real calendar date
//     (Feb 30 is rejected, etc.). Invalid input shows an inline error
//     and sets aria-invalid="true" so screen readers announce it.
//   • A 📅 button beside the input opens the native calendar picker
//     when supported (Chromium, Edge, Firefox≥101) — falls back to
//     focusing the hidden date input on older browsers.

// Insert dashes at canonical positions (after digit 4, after digit 6).
// Non-digits are stripped first so pasting "January 1, 2026" still
// behaves sensibly when the digits parse cleanly.
function autoFormat(input) {
  const digits = String(input == null ? '' : input).replace(/\D/g, '').slice(0, 8);
  let out = digits;
  if (digits.length >= 4) out = digits.slice(0, 4) + '-' + digits.slice(4);
  if (digits.length >= 6) out = out.slice(0, 7) + '-' + out.slice(7);
  return out;
}

// Strict yyyy-mm-dd parser that also validates day-of-month. Empty is
// treated as valid so a not-yet-filled field doesn't flag an error.
function isValidYMD(s) {
  if (!s) return true;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const [, ys, mos, ds] = m;
  const y  = Number(ys);
  const mo = Number(mos);
  const d  = Number(ds);
  if (mo < 1 || mo > 12) return false;
  if (d  < 1 || d  > 31) return false;
  // Round-trip through Date to reject Feb 30 / Apr 31 / non-leap Feb 29.
  const dt = new Date(y, mo - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
}

export default function DateInput({
  value,
  onChange,
  onBlur,
  className,
  disabled,
  required,
  'aria-label': ariaLabel,
  id,
  name,
  ...rest
}) {
  const [text, setText] = useState(value || '');
  const [touched, setTouched] = useState(false);
  const pickerRef = useRef(null);

  // Sync external value into local text when the parent updates value
  // independently (e.g. after a save round-trip or on form reset).
  useEffect(() => {
    setText(value || '');
  }, [value]);

  // Re-validate as the user types so errors clear once the text becomes
  // valid again. Only displayed once the field has been blurred at
  // least once (so a fresh input doesn't flag "incomplete" mid-typing).
  const invalid = touched && text !== '' && !isValidYMD(text);

  function fireChange(newVal) {
    if (typeof onChange === 'function') {
      // Synthesize an event-shaped object so callers using
      // `e.target.value` keep working without changes.
      onChange({ target: { value: newVal } });
    }
  }

  function handleChange(e) {
    const formatted = autoFormat(e.target.value);
    setText(formatted);
    fireChange(formatted);
  }

  function handleBlur(e) {
    setTouched(true);
    if (typeof onBlur === 'function') onBlur(e);
  }

  function openPicker() {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch { /* fall through */ }
    }
    // Older browsers: focusing the native input usually surfaces the
    // calendar UI on click. Click-then-focus is the best we can do.
    el.focus();
    el.click();
  }

  function handlePickerChange(e) {
    // Native date input always yields '' or a valid 'yyyy-mm-dd'.
    const v = e.target.value;
    setText(v);
    fireChange(v);
    setTouched(true);
  }

  // The hidden date input only accepts a valid 'yyyy-mm-dd' value —
  // anything else trips a React warning. Strip to '' when the typed
  // text isn't valid yet.
  const pickerValue = isValidYMD(text) ? text : '';

  return (
    <span className={`date-input-wrap${invalid ? ' date-input-invalid' : ''}`}>
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        value={text}
        id={id}
        name={name}
        disabled={disabled}
        required={required}
        placeholder="yyyy-mm-dd"
        className={`date-input-text ${className || ''}`}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-invalid={invalid ? 'true' : undefined}
        aria-label={ariaLabel}
        autoComplete="off"
      />
      <button
        type="button"
        className="date-input-picker-btn"
        onClick={openPicker}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Open date picker"
        title="Open calendar"
      >
        <Calendar size={14} strokeWidth={1.75} />
      </button>
      <input
        ref={pickerRef}
        type="date"
        className="date-input-hidden-native"
        value={pickerValue}
        onChange={handlePickerChange}
        tabIndex={-1}
        aria-hidden="true"
      />
      {invalid && (
        <span className="date-input-error" role="alert">
          Use YYYY-MM-DD
        </span>
      )}
    </span>
  );
}
